import { computed } from 'vue'

export const generateDashboardCards = (
  participantsByDate,
  participantsByCruise,
  filteredRows,
  selectedFilters
) => {
  const totalParticipants = computed(() => 
    filteredRows.value.reduce((sum, row) => sum + row.participants, 0)
  )

  const chartDataByDate = computed(() => {
    const entries = Object.entries(participantsByDate.value)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    
    const data = entries.map(entry => entry[1])
    
    return {
      labels: entries.map(entry => entry[0]),
      datasets: [{
        label: 'Participants',
        data,
        backgroundColor: '#3b82f6',
        borderColor: '#2563eb',
        borderWidth: 1
      }]
    }
  })

  const chartOptionsByDate = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { 
        display: true, 
        text: 'Participants by Date', 
        font: { size: 16 } 
      },
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const value = context.parsed.y
            const percentage = totalParticipants.value 
              ? `${((value / totalParticipants.value) * 100).toFixed(1)}%`
              : '0%'
            return `Percentage: ${percentage}`
          }
        }
      }
    },
    scales: { 
      y: { 
        beginAtZero: true, 
        title: { display: true, text: 'Participants' } 
      }, 
      x: { 
        title: { display: true, text: 'Date' } 
      } 
    }
  }))

  const chartDataByCruise = computed(() => {
    const dataSource = getChartDataSource(
      selectedFilters.value,
      filteredRows.value
    )
    
    const entries = Object.entries(dataSource)
    const total = totalParticipants.value || 1
    const sorted = entries.sort((a, b) => b[1] - a[1])

    const labels = sorted.map(([label, value]) => {
      const percentage = ((value / total) * 100).toFixed(1)
      return `${label} (${percentage}%)`
    })

    const dataValues = sorted.map(entry => entry[1])

    return {
      labels,
      datasets: [{
        label: getChartLabel(selectedFilters.value),
        data: dataValues,
        backgroundColor: getChartColors(sorted.length),
        borderWidth: 1
      }]
    }
  })

  const chartOptionsByCruise = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { 
        display: true, 
        text: getChartTitle(selectedFilters.value), 
        font: { size: 16 } 
      },
      legend: {
        position: 'bottom',
        labels: {
          generateLabels(chart) {
            const data = chart.data
            return data.labels.map((text, index) => ({
              text,
              fillStyle: data.datasets[0].backgroundColor[index],
              strokeStyle: data.datasets[0].backgroundColor[index],
              lineWidth: 1,
              hidden: false,
              index
            }))
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw
            const percentage = totalParticipants.value
              ? `${((value / totalParticipants.value) * 100).toFixed(1)}%`
              : '0%'
            return `${context.label.replace(/ \(\d+(\.\d+)?%\)/, '')}: ${value} (${percentage})`
          }
        }
      }
    }
  }))

  return {
    chartDataByDate,
    chartOptionsByDate,
    chartDataByCruise,
    chartOptionsByCruise
  }
}

const getChartDataSource = (filters, filteredRows) => {
  if (filters.tour) {
    return filteredRows
      .filter(row => row.name === filters.tour)
      .reduce((acc, row) => {
        acc[row.date] = (acc[row.date] || 0) + row.participants
        return acc
      }, {})
  } 
  
  if (filters.ship) {
    return filteredRows
      .filter(row => row.shipName === filters.ship)
      .reduce((acc, row) => {
        const tourLabel = row.name || '—'
        acc[tourLabel] = (acc[tourLabel] || 0) + row.participants
        return acc
      }, {})
  } 
  
  if (filters.cruise) {
    return filteredRows
      .filter(row => row.cruiseLine === filters.cruise)
      .reduce((acc, row) => {
        acc[row.shipName] = (acc[row.shipName] || 0) + row.participants
        return acc
      }, {})
  }
  
  return filteredRows.reduce((acc, row) => {
    acc[row.cruiseLine] = (acc[row.cruiseLine] || 0) + row.participants
    return acc
  }, {})
}

const getChartLabel = (filters) => {
  if (filters.tour) return 'Participants by Date'
  if (filters.ship) return 'Participants by Tour'
  if (filters.cruise) return 'Participants by Ship'
  return 'Participants by Cruise Line'
}

const getChartTitle = (filters) => {
  if (filters.tour) return `Participants by Date — ${filters.tour}`
  if (filters.ship) return `Participants by Tour — ${filters.ship}`
  if (filters.cruise) return `Participants by Ship — ${filters.cruise}`
  return 'Participants by Cruise Line'
}

export const getChartColors = (count) => {
  const colorPalette = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280',
    '#14b8a6', '#f43f5e', '#a855f7', '#eab308', '#22c55e',
    '#0ea5e9', '#d946ef', '#64748b', '#dc2626', '#ea580c',
    '#ca8a04', '#16a34a', '#0891b2', '#9333ea', '#db2777'
  ]
  
  return Array.from({ length: count }, (_, i) => colorPalette[i % colorPalette.length])
}

export const createDashboardCards = (stats) => {
  if (!stats) return {}

  return {
    mostOccuredTour: { 
      title: 'Most Occurred Tour', 
      data: `${stats.mostOccuredTour || '—'} <br/>${stats.mostOccuredTourCount || 0}` 
    },
    mostOccuredCruiseLine: { 
      title: 'Most Frequent Cruise Line', 
      data: `${stats.mostOccuredCruiseLine || '—'} <br/>${stats.mostOccuredCruiseLineCount || 0}` 
    },
    mostOccuredShip: { 
      title: 'Most Frequent Ship', 
      data: `${stats.mostOccuredShip || '—'} <br/>${stats.mostOccuredShipCount || 0}` 
    },
    totalParticipants: { 
      title: 'Overall Participants in all Ports', 
      data: stats.totalParticipants ?? 0 
    },
    singleTourMaxParticipants: { 
      title: 'Max Participants in Single Tour', 
      data: `${stats.singleTourMaxName || '—'} <br/>${stats.singleTourMaxDate || '—'} <br/>${stats.singleTourMaxParticipants || 0}` 
    },
    highestTourParticipants: { 
      title: 'Highest Total in a Tour', 
      data: `${stats.highestTourParticipants || '—'} <br/>${stats.highestTourParticipantsCount || 0}` 
    },
    totalCruiseLines: { 
      title: 'Number of Cruise Lines', 
      data: stats.uniqueCruiseLines?.length ?? 0 
    },
    totalTours: { 
      title: 'Total Tours', 
      data: stats.totalTours ?? 0 
    },
    shipMostParticipants: { 
      title: 'Ship with Most Participants in a Year', 
      data: `${stats.shipMostParticipants || '—'} <br/>${stats.shipMostParticipantsCount || 0}` 
    },
    cruiseLineMostParticipants: { 
      title: 'Cruise Line with Most Participants in a Year', 
      data: `${stats.cruiseLineMostParticipants || '—'} <br/>${stats.cruiseLineMostParticipantsCount || 0}` 
    },
    mostWorkedEscort: { 
      title: 'Most Worked Escort', 
      data: `${stats.mostWorkedEscort || '—'} <br/>${stats.mostWorkedEscortCount || 0}` 
    },
    mostWorkedGuide: { 
      title: 'Most Worked Guide', 
      data: `${stats.mostWorkedGuide || '—'} <br/>${stats.mostWorkedGuideCount || 0}` 
    },
    shipSingleMax: { 
      title: 'Ship with Most Participants in a Single Call', 
      data: `${stats.shipSingleMaxName || '—'} <br/>${stats.shipSingleMaxDate || '—'} <br/>${stats.shipSingleMaxParticipants || 0}` 
    },
    dateMax: { 
      title: 'Most Participants on Single Date', 
      data: `${stats.dateMaxDate || '—'} <br/>${stats.dateMaxParticipants || 0}<br/>${(stats.dateMaxShips || []).join('<br/>') || 'No ships'}` 
    },
    shipStats: { 
      title: 'Total Ship Calls & Unique Ships Calls', 
      data: `${stats.totalShipCalls || 0} Calls<br/>${stats.uniqueShips?.length || 0} Unique` 
    },
    uniqueStaffWork: { 
      title: 'Staff Assignments', 
      data: `Total: ${stats.totalUniqueStaffDates || 0}<br/>Guides: ${stats.totalUniqueGuidesDates || 0}<br/>Escorts: ${stats.totalUniqueEscortsDates || 0}` 
    },
    avgParticipantsPerTour: {
      title: 'Average Participants per Tour',
      data: Math.round(stats.avgParticipantsPerTour || 0)
    },
    peakMonth: {
      title: 'Peak Month',
      data: `${stats.peakMonth || '—'} <br/>${stats.peakMonthParticipants || 0} participants`
    },
    participantsPerShipCall: {
      title: 'Participants per Ship Call',
      data: `${Math.round(stats.avgParticipantsPerShipCall || 0)}<br/><small>${Math.round(stats.avgToursPerShipCall || 0)} tours per call</small>`
    },
    staffPerParticipant: {
      title: 'Staff per Participant',
      data: stats.totalStaff
        ? `1 : ${Math.round(stats.participantsPerStaff || 0)}<br/><small>${stats.totalStaff} staff</small>`
        : '—'
    }
  }
}